import React, { useEffect, useState } from "react";
import "../../../../views/print/receipt.scss";
import { getDecryptedToken } from "../../../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../../../utils/currency/currencyUtils";
import baseUrl from "../../../../api/baseUrl";
import { getMediaUrl } from "../../../../utils/mediaUrl";

// Map currency symbols to time zones
const currencyTimeZoneMap: Record<string, string> = {
  $: "America/New_York",
  "¥": "Asia/Tokyo",
  "Rs.": "Asia/Colombo",
  "€": "Europe/Berlin",
  "£": "Europe/London",
  "₹": "Asia/Kolkata",
  A$: "Australia/Sydney",
  C$: "America/Toronto",
};

// Helper function to format phone number by removing country code
const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return "";
  let cleaned = phoneNumber.replace(/[\s\-()]/g, "");
  if (cleaned.startsWith("+")) {
    cleaned = cleaned.substring(1);
  }
  const countryCodePatterns = [
    { code: "94", replace: "0" },
    { code: "91", replace: "0" },
    { code: "1", replace: "" },
    { code: "44", replace: "0" },
    { code: "61", replace: "0" },
    { code: "81", replace: "0" },
    { code: "86", replace: "0" },
    { code: "49", replace: "0" },
    { code: "33", replace: "0" },
    { code: "39", replace: "0" },
    { code: "971", replace: "0" },
    { code: "966", replace: "0" },
    { code: "65", replace: "" },
    { code: "60", replace: "0" },
  ];
  for (const pattern of countryCodePatterns) {
    if (cleaned.startsWith(pattern.code)) {
      return pattern.replace + cleaned.substring(pattern.code.length);
    }
  }
  if (!cleaned.startsWith("0") && cleaned.length >= 9) {
    return "0" + cleaned;
  }
  return cleaned;
};

// Format number to 2 decimal places
const formatNumber = (value: any) => Number(value || 0).toFixed(2);

function ReturnRecipt({
  selectedBill,
  returnItems,
  payments,
}: {
  selectedBill: any;
  returnItems: any[];
  payments: any;
}) {
  const shopName = getDecryptedToken("martName");
  const address = getDecryptedToken("address");
  const phoneNumber = getDecryptedToken("phoneNumber");
  const name = getDecryptedToken("name");
  const email = getDecryptedToken("email");
  const isEmailShown = getDecryptedToken("isEmailShown") === "true";
  const registrationNumber = getDecryptedToken("registrationNumber");
  const logo = getDecryptedToken("logo");
  const currency = getDecryptedToken("currency");
  const currencySymbol = getCurrencySymbol();

  const formattedPhoneNumber = formatPhoneNumber(phoneNumber || "");
  const addressNumberMatch = address?.match(/(T\d{13,})/);
  const addressNumber = addressNumberMatch ? addressNumberMatch[1] : null;

  const [currentTime, setCurrentTime] = useState(new Date());
  const timeZone =
    currencyTimeZoneMap[currency as string] ||
    Intl.DateTimeFormat().resolvedOptions().timeZone;

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  if (!selectedBill || !returnItems || returnItems.length === 0) return null;

  const applyInline =
    typeof shopName === "string" &&
    (shopName.trim().toLowerCase() === "mmk chiba" ||
      shopName.trim().toLowerCase() === "ayman bakery restaurant" ||
      shopName.trim().toLowerCase() === "world trading asian food");

  // Calculate total return amount
  const totalReturn = returnItems.reduce((sum, item) => {
    const price = Number(item.givenPrice || item.price || 0);
    const deduction = Number(item.deduction || 0);
    const qty = Number(item.quantity || 0);
    return sum + (price - deduction) * qty;
  }, 0);

  return (
    <div className={`receipt${applyInline ? " mmk-chiba" : ""}`}>
      <div className="headers">
        {logo && logo !== "" && (
          <div className="logo">
            <img
              src={getMediaUrl(logo)}
              alt="Shop Logo"
              style={{
                width: "80px",
                height: "80px",
                objectFit: "contain",
                border: "1px solid #e2e8f0",
                borderRadius: "4px",
                padding: "4px",
                marginBottom: "10px",
              }}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>
        )}
        <div className="title">{shopName}</div>
        <div className="address">{address}</div>
        {addressNumber && <div className="address-number">{addressNumber}</div>}
        {registrationNumber && (
          <div className="registration-number">
            Reg No: {registrationNumber}
          </div>
        )}
        <div className="tell">{formattedPhoneNumber}</div>
        {isEmailShown && email && <div className="email">{email}</div>}
        <div className="bilNUmber">
          {payments?.billNumber || selectedBill?.payments?.billNumber}
        </div>
        <div className="sale-type return">RETURN RECEIPT</div>
        <div className="time_r">
          <div className="date_r">
            {currentTime.toLocaleDateString(undefined, {
              year: "numeric",
              month: "short",
              day: "2-digit",
              timeZone,
            }) +
              " " +
              currentTime.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
                timeZone,
              })}
          </div>
          <div className="name_r">{name}</div>
        </div>
      </div>

      <hr />

      <div className="heading">
        <div className="row1">
          <span>Ln</span>
          <span>items</span>
        </div>
        <div className="row2">
          <span>qty</span>
          <span>rate</span>
          <span>gross</span>
          <span>deduct</span>
          <span>net amt</span>
        </div>
      </div>

      <hr />

      {returnItems.map((item: any, i: number) => {
        const itemName =
          item?.Item?.name || item?.["Item.name"] || item?.itemName || "Item";
        const qty = Number(item.quantity || 0);
        const price = Number(item.givenPrice || item.price || 0);
        const deduction = Number(item.deduction || 0);
        const gross = price * qty;
        const netAmt = (price - deduction) * qty;

        return (
          <div className="r_items" key={i}>
            <div className="row1">
              <span>{String(i + 1).padStart(2, "0")}.</span>
              <span>{itemName}</span>
            </div>
            <div className="row2">
              <span>{formatNumber(qty)}</span>
              <span>
                {currencySymbol} {formatNumber(price)}
              </span>
              <span>
                {currencySymbol} {formatNumber(gross)}
              </span>
              <span>{formatNumber(deduction)}</span>
              <span>
                {currencySymbol} {formatNumber(netAmt)}
              </span>
            </div>
          </div>
        );
      })}

      <hr />

      <div className="amounts">
        <div className="datas">
          <span>Total Return:</span>
          <span>
            {currencySymbol} {formatNumber(totalReturn)}
          </span>
        </div>
      </div>

      <hr />

      <div className="thank">Return Receipt - Thank You!</div>
    </div>
  );
}

export default ReturnRecipt;
